import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { useAuth } from '../../contexts/AuthContext';
import {
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  UserIcon,
  PhoneIcon,
  ChevronUpDownIcon,
  CheckIcon,
  XMarkIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

// --- Обновленный кастомный селект ---
const CustomSelect = ({ value, onChange, options }) => {
  const selected = options.find(opt => opt.value === value) || options[0];

  return (
    <Listbox value={value} onChange={onChange}>
      {/* min-w-[180px] не дает компоненту прыгать по ширине при смене текста */}
      <div className="relative w-full lg:w-[200px]">
        <Listbox.Button className="relative w-full cursor-pointer rounded-xl bg-zinc-50 py-3.5 pl-12 pr-10 text-left border-none focus:ring-2 focus:ring-orange-500/20 transition-all shadow-sm hover:bg-zinc-100/50">
          {/* Иконка календаря как визуальный якорь */}
          <CalendarDaysIcon className="h-5 w-5 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
          
          <span className="block truncate text-sm font-black text-zinc-900 uppercase tracking-tight">
            {selected.label}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            <ChevronUpDownIcon className="h-4 w-4 text-zinc-400" aria-hidden="true" />
          </span>
        </Listbox.Button>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-50 mt-2 max-h-60 w-full overflow-auto rounded-[24px] bg-white p-2 text-base shadow-2xl shadow-zinc-200/60 ring-1 ring-zinc-100 focus:outline-none sm:text-sm border border-zinc-50">
            {options.map((option, idx) => (
              <Listbox.Option
                key={idx}
                className={({ active }) =>
                  `relative cursor-pointer select-none py-3.5 pl-10 pr-4 rounded-xl transition-all ${
                    active ? 'bg-zinc-900 text-white shadow-md' : 'text-zinc-900'
                  }`
                }
                value={option.value}
              >
                {({ selected, active }) => (
                  <>
                    <span className={`block truncate font-black uppercase text-[11px] tracking-wide ${selected ? (active ? 'text-white' : 'text-orange-500') : ''}`}>
                      {option.label}
                    </span>
                    {selected ? (
                      <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-orange-500'}`}>
                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
};

const CompanyBookingsPage = () => {
  const { apiCall } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const statusConfig = {
    pending: { label: 'Pending', color: 'bg-amber-50 text-amber-700 border-amber-100', icon: ClockIcon, actions: ['confirmed', 'cancelled', 'rejected'] },
    confirmed: { label: 'Confirmed', color: 'bg-blue-50 text-blue-700 border-blue-100', icon: CheckCircleIcon, actions: ['in_progress', 'cancelled'] },
    in_progress: { label: 'In Progress', color: 'bg-indigo-50 text-indigo-700 border-indigo-100', icon: ClockIcon, actions: ['completed', 'cancelled'] },
    completed: { label: 'Completed', color: 'bg-emerald-50 text-emerald-700 border-emerald-100', icon: CheckCircleIcon, actions: [] },
    cancelled: { label: 'Cancelled', color: 'bg-rose-50 text-rose-700 border-rose-100', icon: XCircleIcon, actions: [] },
    rejected: { label: 'Rejected', color: 'bg-zinc-100 text-zinc-600 border-zinc-200', icon: ExclamationTriangleIcon, actions: [] },
    rescheduled: { label: 'Rescheduled', color: 'bg-orange-50 text-orange-700 border-orange-100', icon: CalendarDaysIcon, actions: ['confirmed', 'cancelled'] }
  };

  const dateFilters = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'tomorrow', label: 'Tomorrow' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' }
  ];

  useEffect(() => { fetchBookings(); }, [statusFilter, dateFilter]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const response = await apiCall(`/companies/bookings?${params.toString()}`);
      if (response.success) setBookings(response.bookings || response.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const response = await apiCall(`/companies/bookings/${bookingId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      if (response.success) fetchBookings();
    } catch (error) {
      console.error(error);
    }
  };

  const filteredBookings = useMemo(() => {
    return bookings.filter(booking => {
      const searchLower = searchTerm.toLowerCase();
      const clientName = (booking.client_name || booking.Customer?.FirstName || '').toLowerCase();
      const serviceName = (booking.service_name || booking.ServiceName || '').toLowerCase();
      return clientName.includes(searchLower) || serviceName.includes(searchLower);
    });
  }, [bookings, searchTerm]);

  if (loading) return <LoadingState />;

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 animate-in fade-in duration-500 p-4 lg:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-4xl font-black text-zinc-900 tracking-tight">Orders</h1>
          <p className="text-zinc-500 font-medium mt-1">Manage client requests and service history</p>
        </div>
        <div className="flex items-center gap-2 bg-white p-1.5 rounded-2xl border border-zinc-100 shadow-sm">
           <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400 px-3">Total Activity</span>
           <div className="bg-zinc-900 text-white px-4 py-1.5 rounded-xl text-sm font-bold shadow-lg shadow-zinc-200">
             {bookings.length}
           </div>
        </div>
      </div>

      {/* Modern Status Chips */}
      <div className="flex flex-wrap gap-2 pb-2">
        <StatusChip 
          label="All Orders" 
          active={statusFilter === 'all'} 
          onClick={() => setStatusFilter('all')} 
          count={bookings.length}
        />
        {Object.entries(statusConfig).map(([key, config]) => (
          <StatusChip 
            key={key}
            label={config.label}
            active={statusFilter === key}
            onClick={() => setStatusFilter(key)}
            count={bookings.filter(b => (b.status || b.Status) === key).length}
          />
        ))}
      </div>

      {/* Filter Bar: flex-row позволяет селекту не растягиваться */}
      <div className="flex flex-col lg:flex-row gap-4 bg-white p-3 rounded-[24px] border border-zinc-100 shadow-sm">
        <div className="flex-grow relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            placeholder="Search by client, service or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-14 pr-4 py-3.5 bg-zinc-50 border-none rounded-xl focus:ring-2 focus:ring-orange-500/10 transition-all text-sm font-bold text-zinc-900 placeholder:text-zinc-300 shadow-inner"
          />
        </div>
        <div className="shrink-0 flex items-center">
          <CustomSelect 
            value={dateFilter}
            onChange={setDateFilter}
            options={dateFilters}
          />
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bg-white rounded-[32px] border border-zinc-100 shadow-xl shadow-zinc-200/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50/50">
                <th className="px-8 py-5 text-left text-[10px] font-black text-zinc-400 uppercase tracking-[0.2em]">Client & Service</th>
                <th className="px-8 py-5 text-left text-[10px] font-black text-zinc-400 uppercase tracking-[0.2em]">Schedule</th>
                <th className="px-8 py-5 text-left text-[10px] font-black text-zinc-400 uppercase tracking-[0.2em]">Amount</th>
                <th className="px-8 py-5 text-left text-[10px] font-black text-zinc-400 uppercase tracking-[0.2em]">Status</th>
                <th className="px-8 py-5 text-right text-[10px] font-black text-zinc-400 uppercase tracking-[0.2em]">Manage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-50">
              {filteredBookings.map((booking) => (
                <BookingRow 
                  key={booking.id || booking.ID} 
                  booking={booking} 
                  config={statusConfig}
                  onView={() => { setSelectedBooking(booking); setShowModal(true); }}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && selectedBooking && (
        <DetailModal 
          booking={selectedBooking} 
          config={statusConfig} 
          onClose={() => setShowModal(false)}
          onStatusUpdate={updateBookingStatus}
        />
      )}
    </div>
  );
};

// --- Вспомогательные компоненты ---

const StatusChip = ({ label, active, onClick, count }) => (
  <button
    onClick={onClick}
    className={`px-5 py-2.5 rounded-xl text-xs font-black transition-all border ${
      active 
        ? 'bg-zinc-900 text-white border-zinc-900 shadow-lg shadow-zinc-200 scale-105' 
        : 'bg-white text-zinc-400 border-zinc-100 hover:border-zinc-300 shadow-sm'
    }`}
  >
    {label} <span className={`ml-2 ${active ? 'text-orange-400' : 'text-zinc-300'}`}>{count}</span>
  </button>
);

const BookingRow = ({ booking, config, onView }) => {
  const status = config[booking.status || booking.Status] || config.pending;
  const date = new Date(booking.date_time || booking.DateTime);

  return (
    <tr className="group hover:bg-zinc-50/50 transition-colors">
      <td className="px-8 py-6">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-2xl bg-zinc-100 flex items-center justify-center text-zinc-400 font-bold group-hover:bg-white group-hover:shadow-sm transition-all text-lg">
            {booking.client_name?.[0] || 'U'}
          </div>
          <div>
            <div className="text-sm font-black text-zinc-900">{booking.client_name || 'Client'}</div>
            <div className="text-xs font-bold text-orange-500 mt-0.5">{booking.service_name}</div>
          </div>
        </div>
      </td>
      <td className="px-8 py-6">
        <div className="text-sm font-bold text-zinc-700">{date.toLocaleDateString('ru-RU')}</div>
        <div className="text-xs font-medium text-zinc-400 mt-1">{date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
      </td>
      <td className="px-8 py-6 text-sm font-black text-zinc-900">
        ${booking.price || booking.Price}
      </td>
      <td className="px-8 py-6">
        <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider border ${status.color}`}>
          {status.label}
        </span>
      </td>
      <td className="px-8 py-6 text-right">
        <button onClick={onView} className="p-2.5 hover:bg-white hover:shadow-md rounded-xl transition-all text-zinc-400 hover:text-zinc-900">
          <EyeIcon className="h-5 w-5" />
        </button>
      </td>
    </tr>
  );
};

const LoadingState = () => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
    <div className="w-12 h-12 border-4 border-zinc-100 border-t-orange-500 rounded-full animate-spin"></div>
    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-400">Loading Orders</p>
  </div>
);

const DetailModal = ({ booking, config, onClose, onStatusUpdate }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/60 backdrop-blur-md animate-in fade-in duration-300">
    <div className="bg-white w-full max-w-2xl rounded-[40px] shadow-2xl overflow-hidden border border-zinc-100">
      <div className="p-8 border-b border-zinc-50 flex justify-between items-center bg-zinc-50/30">
        <div>
          <h2 className="text-2xl font-black text-zinc-900 tracking-tight">Order Details</h2>
          <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-1">ID: {booking.id || booking.ID}</p>
        </div>
        <button onClick={onClose} className="p-3 bg-white rounded-2xl shadow-sm border border-zinc-100 hover:rotate-90 transition-all duration-300 text-zinc-400 hover:text-zinc-900">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      
      <div className="p-10 grid grid-cols-1 md:grid-cols-2 gap-10">
        <div className="space-y-6">
          <DetailItem icon={<UserIcon/>} label="Client" value={booking.client_name} />
          <DetailItem icon={<PhoneIcon/>} label="Contact" value={booking.customer_info?.phone || 'N/A'} />
          <DetailItem icon={<CalendarDaysIcon/>} label="Service" value={booking.service_name} color="text-orange-500" />
        </div>
        <div className="space-y-6">
          <DetailItem icon={<ClockIcon/>} label="Duration" value={`${booking.duration || 0} min`} />
          <DetailItem icon={<CurrencyDollarIcon/>} label="Total Price" value={`$${booking.price || 0}`} />
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 block mb-2">Change Status</label>
            <div className="flex flex-wrap gap-2">
               {config[booking.status || booking.Status]?.actions.length > 0 ? (
                 config[booking.status || booking.Status].actions.map(action => (
                   <button 
                     key={action}
                     onClick={() => { onStatusUpdate(booking.id || booking.ID, action); onClose(); }}
                     className="px-4 py-2 bg-zinc-100 hover:bg-zinc-900 hover:text-white rounded-xl text-xs font-bold transition-all shadow-sm"
                   >
                     {config[action].label}
                   </button>
                 ))
               ) : (
                 <span className="text-xs font-medium text-zinc-400 italic">No actions available</span>
               )}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const DetailItem = ({ icon, label, value, color = "text-zinc-900" }) => (
  <div className="flex items-start gap-4">
    <div className="p-3 bg-zinc-50 rounded-xl text-zinc-400">{React.cloneElement(icon, { className: 'h-5 w-5' })}</div>
    <div>
      <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400">{label}</div>
      <div className={`text-sm font-black mt-0.5 ${color}`}>{value}</div>
    </div>
  </div>
);

export default CompanyBookingsPage;